import { NavLink } from "react-router-dom";
import { FolderClosed, Plus } from "lucide-react";

import { detectTextLang, translations } from "utils/i18n";
import { useLanguage } from "context/languageContext";
import { cn } from "lib/utils";
import { Skeleton } from "components/ui/skeleton";
import { SidebarTrigger } from "components/ui/sidebar";

// text-foreground/80 doesn't work here — --fg is a plain hex custom property,
// not the space-separated-channel format Tailwind's opacity modifier needs to
// compose with, so it silently resolves to full opacity. A literal shade
// between --fg (#f2f2f2) and --fg-muted (#969696) instead.
const ROW =
  "flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-[#c4c4c4] no-underline transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
const ROW_ACTIVE = "bg-accent font-medium text-accent-foreground";

/**
 * The secondary panel shown alongside the Projects section — ported from
 * arkii-product's LibraryPanel. Only the Projects tier made the trip: this
 * app already has "all files" as Home, its own top-level tab, so there's no
 * "All files" row or Trash tier to carry over here.
 */
export default function ProjectsPanel({ projects, counts, loading, onCreateClick, fullWidth = false }) {
  const { language } = useLanguage();
  const tr = translations[language].projectsPanel;

  return (
    <aside
      className={cn(
        "flex flex-col bg-card",
        fullWidth ? "w-full" : "w-44 shrink-0 border-e border-border sm:w-52 xl:w-60"
      )}
    >
      {/* Same shape as the app's ContentHeader (see layout.js) — Projects
          skips that one so this panel can run the full height instead of
          being cut short below it, so the trigger lives here now. */}
      <div className="flex h-12 shrink-0 items-center gap-2 border-b border-border px-3">
        <SidebarTrigger className="text-muted-foreground" />
        <span className="text-sm font-semibold text-foreground">{tr.title}</span>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
        <nav className="grid gap-0.5">
          <button type="button" data-slot="button" onClick={onCreateClick} className={cn(ROW, "border-0 bg-transparent")}>
            <Plus className="size-4 shrink-0" aria-hidden="true" />
            <span className="flex-1 truncate text-start">{tr.newProject}</span>
          </button>

          {loading ? (
            <div className="grid gap-1 pt-1">
              {Array.from({ length: 3 }).map((_, index) => (
                <Skeleton key={index} className="h-7 w-full" />
              ))}
            </div>
          ) : (
            <>
              {projects.map((project) => (
                <NavLink
                  key={project.id}
                  to={`/projects/${project.id}`}
                  className={({ isActive }) => cn(ROW, isActive && ROW_ACTIVE)}
                >
                  <FolderClosed className="size-4 shrink-0" aria-hidden="true" />
                  <span lang={detectTextLang(project.name)} className="min-w-0 flex-1 truncate">
                    {project.name}
                  </span>
                  {counts[project.id] ? (
                    <span className="font-mono text-xs tabular-nums text-muted-foreground">{counts[project.id]}</span>
                  ) : null}
                </NavLink>
              ))}
              {projects.length === 0 ? (
                <p className="px-2 py-1.5 text-xs text-muted-foreground">{tr.empty}</p>
              ) : null}
            </>
          )}
        </nav>
      </div>
    </aside>
  );
}
